import api from './api';

export interface LoginCredentials {
    usernameOrEmail: string;
    password: string;
}

export interface RegisterCredentials {
    username: string;
    email: string;
    password: string;
}

export interface User {
    id: number;
    username: string;
    email: string;
    avatar: string | null;
    roles: string[];
    isVipActive: boolean;
    vipExpirationDate: string | null;
    createdAt?: string;
}

export interface AuthResponse {
    token: string;
    type: string;
    id: number;
    username: string;
    email: string;
    avatar: string | null;
    roles: string[];
    vip: boolean;
    createdAt?: string;
}

// 网页版特有的认证状态管理
class WebAuthManager {
    private static instance: WebAuthManager;
    private authListeners: Array<(isAuthenticated: boolean) => void> = [];

    static getInstance(): WebAuthManager {
        if (!WebAuthManager.instance) {
            WebAuthManager.instance = new WebAuthManager();
        }
        return WebAuthManager.instance;
    }

    // 添加认证状态监听器
    addAuthListener(listener: (isAuthenticated: boolean) => void) {
        this.authListeners.push(listener);
    }

    // 移除认证状态监听器
    removeAuthListener(listener: (isAuthenticated: boolean) => void) {
        this.authListeners = this.authListeners.filter(l => l !== listener);
    }

    // 通知所有监听器
    private notifyAuthChange(isAuthenticated: boolean) {
        this.authListeners.forEach(listener => listener(isAuthenticated));
    }

    // 设置认证状态
    setAuthState(isAuthenticated: boolean) {
        this.notifyAuthChange(isAuthenticated);
    }
}

const authManager = WebAuthManager.getInstance();

// 基于 JWT 过期时间的自动登出定时器
let logoutTimer: number | null = null;

function clearLogoutTimer() {
    if (logoutTimer) {
        window.clearTimeout(logoutTimer);
        logoutTimer = null;
    }
}

function decodeJwt(token: string): any | null {
    try {
        const base64Url = token.split('.')[1];
        if (!base64Url) return null;
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
}

function getTokenExpirationMs(token: string | null): number | null {
    if (!token) return null;
    const payload = decodeJwt(token);
    const expSec = payload?.exp;
    if (!expSec || typeof expSec !== 'number') return null;
    return expSec * 1000;
}

function scheduleAutoLogout(token: string | null) {
    clearLogoutTimer();
    const expMs = getTokenExpirationMs(token);
    if (!expMs) return;
    const now = Date.now();
    const delay = Math.max(0, expMs - now);
    // 提前 2 秒触发，避免边界时间抖动
    const safeDelay = Math.max(0, delay - 2000);
    logoutTimer = window.setTimeout(() => {
        try {
            authService.logout();
            // 友好提示
            if (typeof window !== 'undefined') {
                try {
                    // 动态加载 antd 消息以避免循环依赖
                    // eslint-disable-next-line @typescript-eslint/no-var-requires
                    const { message } = require('antd');
                    message.error('登录已过期，请重新登录');
                } catch { }
            }
        } catch { }
    }, safeDelay);
}

const authService = {
    async login(usernameOrEmail: string, password: string, rememberMe: boolean = false): Promise<AuthResponse> {
        try {
            const response = await api.post('/auth/login', {
                usernameOrEmail,
                password
            });

            const authData = response.data;

            const user = {
                id: authData.id,
                username: authData.username,
                email: authData.email,
                avatar: authData.avatar,
                roles: authData.roles,
                isVipActive: authData.vip,
                vipExpirationDate: null,
                createdAt: authData.createdAt
            };

            // 记住我：使用localStorage；未勾选则使用sessionStorage
            const primaryStorage = rememberMe ? localStorage : sessionStorage;
            const fallbackStorage = rememberMe ? sessionStorage : localStorage;

            // iOS Safari 兼容：若primary失败，尝试fallback
            try {
                primaryStorage.setItem('token', authData.token);
                primaryStorage.setItem('user', JSON.stringify(user));
            } catch (primaryErr) {
                try {
                    fallbackStorage.setItem('token', authData.token);
                    fallbackStorage.setItem('user', JSON.stringify(user));
                } catch (fallbackErr) {
                    // 静默处理
                }
            }

            // 安排基于过期时间的自动登出
            scheduleAutoLogout(authData.token);

            // 通知认证状态变化
            authManager.setAuthState(true);

            return authData;
        } catch (error) {
            throw error;
        }
    },

    async register(username: string, email: string, password: string): Promise<any> {
        return api.post('/auth/register', {
            username,
            email,
            password
        });
    },

    // 发送验证码
    async sendVerificationCode(email: string, type: 'REGISTRATION' | 'PASSWORD_RESET'): Promise<any> {
        const response = await api.post('/auth/send-verification-code', {
            email,
            type
        });
        return response.data;
    },

    // 验证验证码
    async verifyCode(email: string, code: string, type: 'REGISTRATION' | 'PASSWORD_RESET'): Promise<any> {
        const response = await api.post('/auth/verify-code', {
            email,
            code,
            type
        });
        return response.data;
    },

    logout(): void {
        clearLogoutTimer();
        // iOS Safari兼容性：清除所有认证相关的存储数据
        try {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('rememberedUser');
        } catch (error) {
            // 静默处理localStorage清理失败
        }

        try {
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('rememberedUser');
        } catch (error) {
            // 静默处理sessionStorage清理失败
        }

        // 清除API缓存
        if (typeof window !== 'undefined' && (window as any).apiUtils) {
            (window as any).apiUtils.clearCache();
        }

        // 通知认证状态变化
        authManager.setAuthState(false);

        // 触发用户信息更新事件
        this.triggerUserInfoUpdate();
    },

    // 触发用户信息更新事件
    triggerUserInfoUpdate(): void {
        window.dispatchEvent(new CustomEvent('userInfoUpdated'));
    },

    // 更新用户信息并触发事件
    updateUserInfo(updatedUser: User): void {
        localStorage.setItem('user', JSON.stringify(updatedUser));
        this.triggerUserInfoUpdate();
    },

    clearRememberedUser(): void {
        localStorage.removeItem('rememberedUser');
    },

    getRememberedUser(): string | null {
        return localStorage.getItem('rememberedUser');
    },

    getCurrentUser(): User | null {
        // 优先从 localStorage 读取，若没有则从 sessionStorage 读取
        let userStr: string | null = null;
        try {
            userStr = localStorage.getItem('user');
        } catch { }

        if (!userStr) {
            try {
                userStr = sessionStorage.getItem('user');
            } catch { }
        }

        if (!userStr) return null;

        try {
            return JSON.parse(userStr);
        } catch {
            // 清理损坏的数据
            try {
                localStorage.removeItem('user');
                sessionStorage.removeItem('user');
            } catch { }
            return null;
        }
    },

    isAuthenticated(): boolean {
        // iOS Safari兼容性：尝试localStorage，失败则尝试sessionStorage
        let token = null;
        try {
            token = localStorage.getItem('token');
        } catch (error) {
            try {
                token = sessionStorage.getItem('token');
            } catch (sessionError) {
                return false;
            }
        }

        const user = this.getCurrentUser();
        return !!(token && user);
    },

    getToken(): string | null {
        // iOS Safari兼容性：尝试localStorage，失败则尝试sessionStorage
        try {
            return localStorage.getItem('token');
        } catch (error) {
            try {
                return sessionStorage.getItem('token');
            } catch (sessionError) {
                return null;
            }
        }
    },

    hasRole(role: string): boolean {
        const user = this.getCurrentUser();
        return user?.roles.includes(role) || false;
    },

    isAdmin(): boolean {
        return this.hasRole('ROLE_ADMIN');
    },

    async refreshUserInfo(): Promise<User> {
        const response = await api.get('/users/me');
        const userData = response.data;

        const user = {
            id: userData.id,
            username: userData.username,
            email: userData.email,
            avatar: userData.avatar,
            roles: userData.roles || [],
            isVipActive: userData.isVipActive || userData.vip || false,
            vipExpirationDate: userData.vipExpirationDate || null,
            createdAt: userData.createdAt
        };

        localStorage.setItem('user', JSON.stringify(user));
        this.triggerUserInfoUpdate();

        return user;
    },

    async forgotPassword(email: string): Promise<void> {
        await api.post('/auth/forgot-password', { email });
    },

    async resetPassword(email: string, code: string, newPassword: string): Promise<void> {
        await api.post('/auth/reset-password', { email, code, newPassword });
    },

    // 网页版特有功能

    // 添加认证状态监听器
    addAuthListener(listener: (isAuthenticated: boolean) => void) {
        authManager.addAuthListener(listener);
    },

    // 移除认证状态监听器
    removeAuthListener(listener: (isAuthenticated: boolean) => void) {
        authManager.removeAuthListener(listener);
    },

    // 检查token是否即将过期
    isTokenExpiringSoon(): boolean {
        const token = this.getToken();
        if (!token) return false;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const expirationTime = payload.exp * 1000;
            const currentTime = Date.now();
            const timeUntilExpiration = expirationTime - currentTime;

            // 如果token在5分钟内过期，返回true
            return timeUntilExpiration < 5 * 60 * 1000;
        } catch (error) {
            return true;
        }
    },

    // 自动刷新token
    async autoRefreshToken(): Promise<boolean> {
        if (!this.isAuthenticated() || !this.isTokenExpiringSoon()) {
            return true;
        }

        try {
            const response = await api.post('/auth/refresh');
            const newToken = response.data.token;
            localStorage.setItem('token', newToken);
            // 刷新后重置自动登出定时器
            scheduleAutoLogout(newToken);
            return true;
        } catch (error) {
            this.logout();
            return false;
        }
    },

    // 验证邮箱
    async verifyEmail(email: string, code: string): Promise<void> {
        await api.post('/auth/verify-email', { email, code });
    },

    // 重新发送验证邮件
    async resendVerificationEmail(email: string): Promise<void> {
        await api.post('/auth/resend-verification', { email });
    }
};

// 网页版自动token刷新
if (typeof window !== 'undefined') {
    // 应用加载时，如果已有 token，则安排一次自动登出
    try {
        const existingToken = localStorage.getItem('token') || sessionStorage.getItem('token');
        // 若已过期则立刻登出
        const exp = getTokenExpirationMs(existingToken);
        if (exp && exp <= Date.now()) {
            authService.logout();
        } else {
            scheduleAutoLogout(existingToken);
        }
    } catch { }

    // 每5分钟检查一次token状态
    setInterval(() => {
        authService.autoRefreshToken();
    }, 5 * 60 * 1000);
}

export default authService;
